import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/utils/cn'

interface PaginationProps {
  page: number
  pageSize: number
  totalItems: number
  onPageChange: (page: number) => void
  className?: string
}

/**
 * Construye la secuencia de páginas con elipsis: siempre la primera y la última,
 * la actual ±1, y `'…'` donde haya hueco. Ej. (current=5, total=20) →
 * `[1, '…', 4, 5, 6, '…', 20]`.
 */
function getPageRange(current: number, total: number): (number | '…')[] {
  if (total <= 1) return [1]
  const delta = 1
  const left = Math.max(2, current - delta)
  const right = Math.min(total - 1, current + delta)
  const pages: (number | '…')[] = [1]
  if (left > 2) pages.push('…')
  for (let i = left; i <= right; i++) pages.push(i)
  if (right < total - 1) pages.push('…')
  pages.push(total)
  return pages
}

/**
 * Paginación numerada reutilizable: resumen "desde–hasta de total" + Anterior,
 * números con elipsis, y Siguiente. La página activa se resalta. Se oculta si no
 * hay items; con una sola página muestra solo el resumen.
 */
export function Pagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  className,
}: PaginationProps) {
  if (totalItems === 0) return null

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, totalItems)
  const pages = getPageRange(page, totalPages)

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3 pt-2', className)}>
      <p className="text-xs text-ink-muted">
        {from}–{to} de {totalItems}
      </p>

      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            aria-label="Página anterior"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {pages.map((p, i) =>
            p === '…' ? (
              <span
                key={`ellipsis-${i}`}
                className="px-1.5 text-sm text-ink-subtle"
                aria-hidden="true"
              >
                …
              </span>
            ) : (
              <Button
                key={p}
                variant={p === page ? 'primary' : 'outline'}
                size="sm"
                className="min-w-9"
                aria-label={`Página ${p}`}
                aria-current={p === page ? 'page' : undefined}
                onClick={() => onPageChange(p)}
              >
                {p}
              </Button>
            ),
          )}

          <Button
            variant="outline"
            size="sm"
            aria-label="Página siguiente"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}
